// src/components/CloudAnomalyAlertsTable.tsx

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { CloudAnomalyAlert, AlertFilters } from "../types/alert";
import { AlertGrouping } from "../components/alerts/AlertGrouping";
import { AlertDetails } from "../components/alerts/AlertDetails";
import { AlertStats } from "../components/alerts/AlertStats";
import { AlertsTimeline } from "../components/alerts/AlertsTimeline";
import { AlertAnalytics } from "../components/alerts/AlertAnalytics";
import { NGROK } from "../../../../APIs";
import CloudFilters from "../../CloudFilters";
import { Button } from "../../../ui/button";
import AlertTrendPanel from "../../Cloud/AlertTrendPanel";
import { getAlertManagementPath } from "../../CloudDashboard/lib/cloudRoutes";
import { useNavigate } from "react-router-dom";
import { BellRing } from "lucide-react";
import "../../Cloud/CloudStatus.css";

const TIME_RANGES = [
  { code: 1, value: "30m", label: "Last 30 minutes" },
  { code: 2, value: "1h", label: "Last 1 hour" },
  { code: 3, value: "4h", label: "Last 4 hours" },
  { code: 4, value: "12h", label: "Last 12 hours" },
  { code: 5, value: "24h", label: "Last 24 hours" },
  { code: 6, value: "7d", label: "Last 7 days" },
  { code: 7, value: "30d", label: "Last 30 days" },
  { code: 8, value: "60d", label: "Last 60 days" },
  { code: 9, value: "1y", label: "Last 1 year" },
];

interface CloudAnomalyAlertsTableProps {
  cloud: "AWS" | "GCP" | "AZURE" | null;
  cloudId: string | null;
  tenantName: string | null;
  apiBaseUrl?: string;
  tenantList?: Array<{ tenantName: string }>;
  cloudIdList?: string[];
  onTenantChange?: (tenantName: string) => void;
  onCloudIdChange?: (cloudId: string) => void;
}

interface AnalyticsData {
  topAlerts: Record<string, number>;
  topAlertTypes: Record<string, number>;
  topRiskyEntities: Record<string, number>;
}

const CloudAnomalyAlertsTable: React.FC<CloudAnomalyAlertsTableProps> = ({
  cloud,
  cloudId,
  tenantName,
  apiBaseUrl = NGROK,
  tenantList = [],
  cloudIdList = [],
  onTenantChange,
  onCloudIdChange,
}) => {
  const navigate = useNavigate();

  const [timeRange] = useState(
    () => TIME_RANGES.find((t) => t.code === 7)?.value || TIME_RANGES[0].value
  );

  const [alerts, setAlerts] = useState<CloudAnomalyAlert[]>([]);
  const [groupingMode, setGroupingMode] = useState<"alertType" | "alert" | "entity">("alert");
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [queryTenantName, setQueryTenantName] = useState<string | null>(tenantName || null);
  const [queryCloudId, setQueryCloudId] = useState<string | null>(cloudId || null);

  // Use CloudFilters state directly if needed, but we can also rely on the parent or handle it here.
  const handleTenantChange = (tenant: string) => {
    setQueryTenantName(tenant);
    if (onTenantChange) onTenantChange(tenant);
  };

  const handleCloudIdChange = (id: string) => {
    setQueryCloudId(id);
    if (onCloudIdChange) onCloudIdChange(id);
  };

  // Analytics data
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    topAlerts: {},
    topAlertTypes: {},
    topRiskyEntities: {},
  });

  // UI state
  const [filters, setFilters] = useState<AlertFilters>({});
  const [selectedAlert, setSelectedAlert] = useState<CloudAnomalyAlert | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [externalGroupingMode, setExternalGroupingMode] = useState<"alertType" | "alert" | "entity" | undefined>(undefined);
  const [externalExpandedGroup, setExternalExpandedGroup] = useState<string | undefined>(undefined);


  const alertsContainerRef = useRef<HTMLDivElement>(null);

  const getTimeRangeCode = (value: string): number => {
    const range = TIME_RANGES.find((t) => t.value === value);
    return range ? range.code : 7; // default "Last 30 days"
  };

  // Sync props with state
  useEffect(() => {
    if (tenantName) setQueryTenantName(tenantName);
  }, [tenantName]);

  useEffect(() => {
    if (cloudId) setQueryCloudId(cloudId);
  }, [cloudId]);

  // Fetch anomalies when params change
  useEffect(() => {
    if (!queryCloudId || !queryTenantName) {
      setAlerts([]);
      return;
    }

    const timeRangeCode = getTimeRangeCode(timeRange);
    const baseParams = {
      tenantName: queryTenantName,
      cloudId: queryCloudId,
      cloudType: cloud,
      timeRangeCode,
    };

    let cancelled = false;

    const fetchAnomalies = async () => {
      try {
        setLoading(true);

        const groupRes = await axios.get(`${apiBaseUrl}/api/cloud/get-anomalies-group`, {
          params: { ...baseParams, groupBy: groupingMode },
        });

        const groupRaw = groupRes.data || [];
        let flattened: CloudAnomalyAlert[] = [];

        if (Array.isArray(groupRaw)) {
          flattened = groupRaw;
        } else {
          Object.values(groupRaw).forEach((value) => {
            if (Array.isArray(value)) flattened = flattened.concat(value as CloudAnomalyAlert[]);
          });
        }

        if (!cancelled) setAlerts(flattened);
      } catch (err) {
        console.error("Error fetching anomalies:", err);
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };

    fetchAnomalies();

    return () => {
      cancelled = true;
    };
  }, [cloud, queryCloudId, queryTenantName, timeRange, groupingMode, apiBaseUrl]);

  // Fetch analytics when time range changes
  useEffect(() => {
    if (!queryCloudId || !queryTenantName) return;

    const timeRangeCode = getTimeRangeCode(timeRange);
    const baseParams = {
      tenantName: queryTenantName,
      cloudId: queryCloudId,
      cloudType: cloud,
      timeRangeCode,
    };

    const fetchAnalytics = async () => {
      try {
        const [topAlertRes, topAlertTypeRes, topRiskyEntitiesRes] = await Promise.all([
          axios.get(`${apiBaseUrl}/api/cloud/get-top-alert`, { params: baseParams }),
          axios.get(`${apiBaseUrl}/api/cloud/get-top-alert-type`, { params: baseParams }),
          axios.get(`${apiBaseUrl}/api/cloud/get-top-risky-entities`, { params: baseParams }),
        ]);

        setAnalyticsData({
          topAlerts: topAlertRes.data || {},
          topAlertTypes: topAlertTypeRes.data || {},
          topRiskyEntities: topRiskyEntitiesRes.data || {},
        });
      } catch (err) {
        console.error("Error fetching anomaly analytics:", err);
      }
    };

    fetchAnalytics();
  }, [cloud, queryCloudId, queryTenantName, timeRange, apiBaseUrl]);

  // Filter alerts based on filters
  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      if (filters.severity?.length && !filters.severity.includes(alert.severity)) {
        return false;
      }
      // Search filter based on grouping mode
      const searchTerm = filters.entity?.toLowerCase();
      if (searchTerm) {
        if (groupingMode === "alert") {
          if (!alert.title.toLowerCase().includes(searchTerm)) {
            return false;
          }
        } else if (groupingMode === "entity") {
          if (!alert.entity.toLowerCase().includes(searchTerm)) {
            return false;
          }
        }
      }
      return true;
    });
  }, [alerts, filters, groupingMode]);

  // Sort alerts by creation date
  const sortedAlerts = useMemo(() => {
    return [...filteredAlerts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [filteredAlerts]);

  const handleViewDetails = (alert: CloudAnomalyAlert) => {
    setSelectedAlert(alert);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedAlert(null);
  };

  const handleAlertTypeClick = useCallback((alertTitle: string) => {
    setExternalGroupingMode("alert");
    setExternalExpandedGroup(alertTitle);

    setTimeout(() => {
      alertsContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 100);
  }, []);

  const handleAlertTypeModeClick = useCallback((alertType: string) => {
    setExternalGroupingMode("alertType");
    setExternalExpandedGroup(alertType);

    setTimeout(() => {
      alertsContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 100);
  }, []);

  const handleEntityClick = useCallback((entity: string) => {
    setExternalGroupingMode("entity");
    setExternalExpandedGroup(entity);

    setTimeout(() => {
      alertsContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 100);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header with Selectors */}
      <div className="pt-6 px-6">
        {cloud && (
          <CloudFilters
            cloudProvider={cloud.toLowerCase()}
            onTenantChange={handleTenantChange}
            onCloudAccountChange={handleCloudIdChange}
            isAdmin={true}
            actionButton={
              <Button
                onClick={() => navigate(getAlertManagementPath(cloud))}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 shadow-md shadow-primary/20 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/30"
                size="default"
              >
                <BellRing className="w-4 h-4" />
                <span className="text-[13px] tracking-wide uppercase">Alert Management</span>
              </Button>
            }
          />
        )}
      </div>

      <main className="max-w-[1600px] mx-auto px-6 pb-8 pt-2 space-y-6">

        {/* Stats — Alert count cards at the top */}
        <AlertStats alerts={sortedAlerts} />


        {/* Alert Trend Analysis */}
        {cloud && <AlertTrendPanel cloudProvider={cloud.toLowerCase()} />}



        {/* Alert Timeline */}
        <AlertsTimeline alerts={sortedAlerts} timeRange={timeRange} />

        {/* 2. Security Alerts Section */}
        {sortedAlerts.length > 0 && (
          <div ref={alertsContainerRef} className="space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="h-8 w-1 bg-primary rounded-full" />
              <h2 className="text-2xl font-bold text-foreground">Security Alerts</h2>
            </div>
            <AlertGrouping
              alerts={sortedAlerts}
              onViewAlertDetails={handleViewDetails}
              externalGroupingMode={externalGroupingMode}
              externalExpandedGroup={externalExpandedGroup}
              searchValue={filters.entity || ""}
              onSearchChange={(value) => setFilters({ ...filters, entity: value })}
              severityFilter={filters.severity || []}
              onSeverityFilterChange={(severity) => setFilters({ ...filters, severity })}
              groupingMode={groupingMode}
              onGroupingModeChange={setGroupingMode}
            />
          </div>
        )}

        {/* 3-6. Analytics Cards */}
        <AlertAnalytics
          alerts={sortedAlerts}
          onAlertTypeClick={handleAlertTypeClick}
          onAlertTypeModeClick={handleAlertTypeModeClick}
          onEntityClick={handleEntityClick}
        />
      </main>

      {/* Alert Details Modal */}
      <AlertDetails alert={selectedAlert} open={isDetailsOpen} onClose={handleCloseDetails} />
    </div>
  );
};

export default CloudAnomalyAlertsTable;
